import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import Box from 'atoms/Box';
import Column from 'atoms/Column';
import MenuButton from 'atoms/MenuButton';
import Row from 'atoms/Row';

import { HeaderLinks } from 'types';
import { ReactComponent as Logo } from 'assets/icons/Logo.svg';

import { ORIGIN } from 'i18n';

import { theme } from 'theme';

export interface HeaderProps {}

function Header({}: HeaderProps) {
  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const { t } = useTranslation();

  const links: HeaderLinks = t('headerLinks', { returnObjects: true });
  const linksArray = Object.values(links);

  const toggleMenu = () => {
    setIsMenuOpened(!isMenuOpened);
  };

  return (
    <HeaderWrapper
      ph={1.5}
      pv={0.75}
      backgroundColor="mirage"
      isMenuOpened={isMenuOpened}
    >
      <HeaderRow justify="between" align="center">
        <ColumnMenu auto>
          <MenuButton onClick={toggleMenu} isOpened={isMenuOpened} />
        </ColumnMenu>
        <ColumnLogo auto>
          <Link href={`https://${ORIGIN}/`}>
            <Logo />
          </Link>
        </ColumnLogo>
        <ColumnEmpty auto />
      </HeaderRow>
      <Menu isMenuOpened={isMenuOpened}>
        {linksArray.map(([text, link], i) => {
          let localLink = link;
          if (ORIGIN.includes('completeanatomy.cn')) {
            localLink = link.replace('3d4medical.com', 'completeanatomy.cn');
          }

          return (
            <MenuItemLink key={i} href={localLink}>
              {text}
            </MenuItemLink>
          );
        })}
      </Menu>
    </HeaderWrapper>
  );
}

export default Header;

const HeaderWrapper = styled(Box)<{ isMenuOpened: boolean }>`
  position: fixed;
  z-index: 99;

  box-sizing: border-box;
  width: 100%;
  height: 100%;
  max-height: 52px;

  transition: max-height 0.2s;

  ${({ isMenuOpened }) =>
    isMenuOpened &&
    css`
      max-height: 100vh;
    `}

  ${theme.devices.desktop`
    display: flex;
    justify-content: space-between;
  `}
`;

const HeaderRow = styled(Row)`
  overflow-x: visible;
`;

const Link = styled.a`
  text-decoration: none;
`;

const MenuItemLink = styled(Link)`
  margin-right: 2.625vw;
  padding: 0.675em 0;

  color: ${theme.colors.white};
  font-weight: 600;
  font-size: 1.5em;
  text-transform: uppercase;

  border-bottom: 1px solid #2b2b2b;

  ${theme.devices.desktop`
    font-size: 0.875em;
  `}
`;

const Menu = styled(Box)<{ isMenuOpened: boolean }>`
  display: flex;
  flex-direction: column;

  max-height: 0;
  margin-top: 1em;

  overflow: hidden;

  opacity: 0;

  transition: 0.2s;

  ${({ isMenuOpened }) =>
    isMenuOpened &&
    css`
      opacity: 1;
      max-height: 100%;
    `}

  ${theme.devices.desktop`
    margin-top: 0;
    flex-direction: row;
    opacity: 1;
    max-height: unset;
  `}
`;

const ColumnMenu = styled(Column)`
  ${theme.devices.desktop`
    display: none;
  `}
`;

const ColumnEmpty = styled(Column)`
  ${theme.devices.desktop`
    display: none;
  `}
`;

const ColumnLogo = styled(Column)`
  width: auto;
  height: 24px;

  ${theme.devices.desktop`
    order: -1;
  `}
`;
